// Favorites — a Set of segment ids the user has starred. The IDs are
// loaded once at startup and updated optimistically on toggle so the
// star icon flips instantly.

import { useCallback, useEffect, useState } from "react";
import {
  fetchFavoriteIds,
  fetchFavorites,
  addFavoriteApi,
  removeFavoriteApi,
} from "../api.js";

export function useFavorites() {
  const [ids, setIds] = useState(() => new Set());
  const [list, setList] = useState([]);   // full data, lazy-loaded
  const [listLoaded, setListLoaded] = useState(false);
  const [loading, setLoading] = useState(true);

  // Initial load: just IDs (cheap), enough to mark palette stars.
  useEffect(() => {
    let cancelled = false;
    fetchFavoriteIds()
      .then((arr) => { if (!cancelled) setIds(new Set(arr)); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const refreshList = useCallback(async () => {
    try {
      const fresh = await fetchFavorites();
      setList(fresh);
      setIds(new Set(fresh.map((f) => f.id)));
      setListLoaded(true);
    } catch {}
  }, []);

  const isFavorite = useCallback((segmentId) => ids.has(segmentId), [ids]);

  const toggle = useCallback(async (segmentId) => {
    const wasFav = ids.has(segmentId);
    // Optimistic update
    setIds((prev) => {
      const next = new Set(prev);
      if (wasFav) next.delete(segmentId);
      else next.add(segmentId);
      return next;
    });
    try {
      if (wasFav) await removeFavoriteApi(segmentId);
      else await addFavoriteApi(segmentId);
      // Mark list as needing refresh next time it's shown.
      setListLoaded(false);
    } catch (err) {
      // Roll back on failure.
      setIds((prev) => {
        const next = new Set(prev);
        if (wasFav) next.add(segmentId);
        else next.delete(segmentId);
        return next;
      });
      console.warn("favorite toggle failed:", err.message);
    }
  }, [ids]);

  return { ids, list, listLoaded, loading, isFavorite, toggle, refreshList };
}
